/* ============================================================
   Nordvital Kvíz — campaign section behaviour
   Custom element <kviz-wheel>. Reads config + questions from embedded JSON,
   renders the gate → intro → wheel → quiz → result flow, and calls the
   backend at /api/nordvital/kviz/* on the configured api_base.

   The wheel outcome is decided SERVER-SIDE. The animation only lands on
   whatever /spin returns — the client never decides the win.
   ============================================================ */
if (!customElements.get('kviz-wheel')) {
  class KvizWheel extends HTMLElement {
    connectedCallback() {
      if (this._init) return;
      this._init = true;

      this.cfg = this._readJson('[data-kviz-config]') || {};
      this.questions = this._readJson('[data-kviz-questions]') || [];
      this.apiBase = (this.cfg.apiBase || '').replace(/\/$/, '');

      this.root = this.querySelector('.kz-root');
      this.state = { email: '', answers: [], current: 0 };

      this.SEG = ['floor', 'lose', 'reformer', 'lose', 'floor', 'lose', 'reformer', 'lose', 'floor', 'lose', 'reformer', 'lose'];
      this.STEP = 360 / this.SEG.length;
      this.currentRot = 0;
      this.spinning = false;

      this._buildWheel();
      this._bind();
      this._go('gate');
    }

    /* ---------- helpers ---------- */
    _readJson(sel) {
      const el = this.querySelector(sel);
      if (!el) return null;
      try {
        return JSON.parse(el.textContent);
      } catch (e) {
        console.warn('[kviz] bad JSON in', sel, e);
        return null;
      }
    }
    $(sel) {
      return this.querySelector(sel);
    }
    _wait(ms) {
      return new Promise((r) => setTimeout(r, ms));
    }

    async _post(path, body) {
      if (!this.apiBase) {
        console.warn('[kviz] api_base not configured — skipping', path);
        return { ok: false };
      }
      try {
        const res = await fetch(`${this.apiBase}/api/nordvital/kviz/${path}`, {
          method: 'POST',
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify(body),
        });
        return await res.json();
      } catch (e) {
        console.error('[kviz] request failed', path, e);
        return { ok: false, error: true };
      }
    }

    /* ---------- navigation ---------- */
    _go(screen) {
      const STAGE = { gate: 0, intro: 1, wheel: 1, quiz: 2, result: 3 };
      this.querySelectorAll('.kz-screen').forEach((s) => s.classList.remove('kz-active'));
      const target = this.$(`#kz-screen-${screen}`);
      if (target) target.classList.add('kz-active');
      this.root.dataset.stage = screen;
      const step = STAGE[screen];
      this.querySelectorAll('.kz-rail span').forEach((el) => {
        const i = Number(el.dataset.step);
        el.classList.toggle('kz-now', i === step);
        el.classList.toggle('kz-done', i < step);
      });
      this.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }

    _bind() {
      this.querySelectorAll('[data-kz-go]').forEach((b) =>
        b.addEventListener('click', () => this._go(b.dataset.kzGo)),
      );

      // 1 · gate
      this.emailEl = this.$('#kz-email');
      this.consentEl = this.$('#kz-consent');
      this.gateBtn = this.$('#kz-gate-btn');
      const validate = () => {
        this.gateBtn.disabled = !(this.emailEl.checkValidity() && this.emailEl.value && this.consentEl.checked);
      };
      this.emailEl.addEventListener('input', validate);
      this.consentEl.addEventListener('change', validate);
      this.$('#kz-gate-form').addEventListener('submit', (e) => this._submitGate(e));

      // 3 · wheel
      this.$('#kz-spin-btn').addEventListener('click', () => this._spin());

      // restart re-runs the quiz only (the wheel is one spin per email)
      const restart = this.$('#kz-restart');
      if (restart) restart.addEventListener('click', () => this._startQuiz());

      // kiosk mode: full reset to the gate for the next player
      const kiosk = this.$('#kz-kiosk-reset');
      if (kiosk) kiosk.addEventListener('click', () => this._reset());
    }

    /* full reset — clears the previous player and returns to the gate (kiosk / events) */
    _reset() {
      this.state = { email: '', answers: [], current: 0 };
      if (this.emailEl) this.emailEl.value = '';
      if (this.consentEl) this.consentEl.checked = false;
      if (this.gateBtn) this.gateBtn.disabled = true;
      const err = this.$('#kz-gate-error');
      if (err) err.textContent = '';
      this.spinning = false;
      const spinBtn = this.$('#kz-spin-btn');
      if (spinBtn) {
        spinBtn.disabled = false;
        spinBtn.textContent = this.cfg.spinButton || 'Pörgetés';
      }
      const overlay = this.$('#kz-reveal');
      if (overlay) overlay.classList.remove('kz-open');
      this._go('gate');
    }

    /* ---------- 1 · gate ---------- */
    async _submitGate(e) {
      e.preventDefault();
      if (this.gateBtn.disabled) return;
      this.state.email = this.emailEl.value.trim().toLowerCase();
      this.gateBtn.disabled = true;
      const errEl = this.$('#kz-gate-error');
      if (errEl) errEl.textContent = '';

      const res = await this._post('subscribe', { email: this.state.email, consent: true });
      if (res && res.ok === false && res.error) {
        if (errEl) errEl.textContent = this.cfg.errorGeneric || 'Hiba történt. Próbáld újra.';
        this.gateBtn.disabled = false;
        return;
      }
      this._go('intro');
    }

    /* ---------- 3 · wheel ---------- */
    _buildWheel() {
      const r = 200;
      const labels = this.cfg.segLabels || { floor: 'Talaj', reformer: 'Reformer', lose: 'Újra' };
      const colors = { floor: '#00213f', reformer: '#ff522e', lose: '#e7dcc8' };
      const textColors = { floor: '#f5efe4', reformer: '#fff', lose: '#00213f' };
      let paths = '';
      let text = '';
      this.SEG.forEach((type, i) => {
        const a0 = ((i * this.STEP - 90) * Math.PI) / 180;
        const a1 = (((i + 1) * this.STEP - 90) * Math.PI) / 180;
        const x0 = r + r * Math.cos(a0);
        const y0 = r + r * Math.sin(a0);
        const x1 = r + r * Math.cos(a1);
        const y1 = r + r * Math.sin(a1);
        paths += `<path d="M${r} ${r} L${x0} ${y0} A${r} ${r} 0 0 1 ${x1} ${y1} Z" fill="${colors[type]}" stroke="#fbf8f2" stroke-width="1.5"/>`;
        const mid = ((i * this.STEP + this.STEP / 2 - 90) * Math.PI) / 180;
        const lr = r * 0.66;
        const lx = r + lr * Math.cos(mid);
        const ly = r + lr * Math.sin(mid);
        const rot = i * this.STEP + this.STEP / 2;
        text += `<text class="kz-seg-label" x="${lx}" y="${ly}" fill="${textColors[type]}" text-anchor="middle" dominant-baseline="middle" transform="rotate(${rot} ${lx} ${ly})">${labels[type]}</text>`;
      });
      this.$('#kz-wheel').innerHTML = `<svg viewBox="0 0 ${r * 2} ${r * 2}">${paths}${text}</svg>`;
    }

    async _spin() {
      if (this.spinning) return;
      this.spinning = true;
      const btn = this.$('#kz-spin-btn');
      btn.disabled = true;
      btn.textContent = this.cfg.spinning || 'Pörög…';

      const outcome = await this._post('spin', { email: this.state.email });
      const won = !!(outcome && outcome.won);
      const prize = outcome ? outcome.prize : null;

      const wanted = won ? prize : 'lose';
      const candidates = this.SEG.map((t, i) => (t === wanted ? i : -1)).filter((i) => i >= 0);
      const target = candidates[Math.floor(Math.random() * candidates.length)] || 0;

      const jitter = (Math.random() - 0.5) * (this.STEP * 0.5);
      const landing = 360 - (target * this.STEP + this.STEP / 2) - jitter;
      this.currentRot += 6 * 360 + (((landing - (this.currentRot % 360)) % 360) + 360) % 360;
      this.$('#kz-wheel').style.transform = `rotate(${this.currentRot}deg)`;

      setTimeout(() => this._reveal(outcome || { won: false, prize: null }), 5500);
    }

    _reveal(outcome) {
      const badge = this.$('#kz-reveal-badge');
      const title = this.$('#kz-reveal-title');
      const text = this.$('#kz-reveal-text');
      const cta = this.$('#kz-reveal-cta');
      const c = this.cfg.reveal || {};
      const prizeName = outcome.prize === 'floor' ? this.cfg.prizeFloor : this.cfg.prizeReformer;
      const win = `<svg viewBox="0 0 24 24"><path d="M5 13l4 4L19 7" stroke-linecap="round" stroke-linejoin="round"/></svg>`;
      const lose = `<svg viewBox="0 0 24 24"><path d="M12 3a9 9 0 1 0 0 18 9 9 0 0 0 0-18zM12 8v5M12 16h.01" stroke-linecap="round"/></svg>`;
      const fill = (tpl) => (tpl || '').replace('{prize}', `<span class="kz-prize-name">${prizeName || ''}</span>`);

      if (outcome.alreadyPlayed) {
        badge.className = outcome.won ? 'kz-reveal__badge' : 'kz-reveal__badge kz-lose';
        badge.innerHTML = outcome.won ? win : lose;
        title.textContent = c.replayTitle || 'Ezt már megpörgetted';
        text.innerHTML = outcome.won ? fill(c.replayWinBody) : c.replayLoseBody || '';
      } else if (outcome.won) {
        badge.className = 'kz-reveal__badge';
        badge.innerHTML = win;
        title.textContent = c.winTitle || 'Nyertél!';
        text.innerHTML = fill(c.winBody);
        this._confetti();
      } else {
        badge.className = 'kz-reveal__badge kz-lose';
        badge.innerHTML = lose;
        title.textContent = c.loseTitle || 'Most nem jött össze';
        text.innerHTML = c.loseBody || '';
      }
      cta.textContent = c.cta || 'Tovább a kvízhez';
      const overlay = this.$('#kz-reveal');
      overlay.classList.add('kz-open');
      cta.onclick = () => {
        overlay.classList.remove('kz-open');
        this._startQuiz();
      };
    }

    _confetti() {
      const colors = ['#ff522e', '#00213f', '#c4ab82', '#f5efe4'];
      for (let i = 0; i < 46; i++) {
        const p = document.createElement('div');
        p.className = 'kz-petal';
        const size = 7 + Math.random() * 9;
        p.style.cssText = `left:${Math.random() * 100}vw;width:${size}px;height:${size * 1.6}px;background:${
          colors[i % colors.length]
        };--kz-spin:${Math.random() * 720 - 360}deg;animation:kz-fall ${2.6 + Math.random() * 2}s ${
          Math.random() * 0.6
        }s cubic-bezier(0.16,1,0.3,1) forwards;`;
        document.body.appendChild(p);
        setTimeout(() => p.remove(), 5200);
      }
    }

    /* ---------- 4 · quiz ---------- */
    _startQuiz() {
      this.state.answers = [];
      this.state.current = 0;
      this._go('quiz');
      this._renderQuestion();
    }
    _renderQuestion() {
      const i = this.state.current;
      const item = this.questions[i];
      if (!item) return this._finish();
      this.$('#kz-q-num').textContent = i + 1;
      this.$('#kz-q-total').textContent = this.questions.length;
      this.$('#kz-q-bar').style.width = `${((i + 1) / this.questions.length) * 100}%`;
      this.$('#kz-q-text').textContent = item.q;
      const wrap = this.$('#kz-q-options');
      wrap.innerHTML = '';
      Object.entries(item.a).forEach(([letter, txt], idx) => {
        if (!txt) return;
        const b = document.createElement('button');
        b.className = 'kz-option';
        b.type = 'button';
        b.style.animationDelay = `${idx * 0.06}s`;
        b.innerHTML = `<span class="kz-letter">${letter}</span><span>${txt}</span>`;
        b.addEventListener('click', () => this._pick(letter, b));
        wrap.appendChild(b);
      });
    }
    _pick(letter, el) {
      if (el.parentElement.querySelector('.kz-picked')) return;
      el.classList.add('kz-picked');
      this.state.answers.push(letter);
      setTimeout(() => {
        if (this.state.current < this.questions.length - 1) {
          this.state.current++;
          this._renderQuestion();
        } else {
          this._finish();
        }
      }, 420);
    }

    /* ---------- scoring + 5 · result ---------- */
    _computeType(answers) {
      const order = ['A', 'B', 'C', 'D', 'E'];
      const counts = { A: 0, B: 0, C: 0, D: 0, E: 0 };
      answers.forEach((l) => counts[l]++);
      let best = order[0];
      order.forEach((l) => {
        if (counts[l] > counts[best]) best = l; // strict > keeps the stronger (earlier) letter on ties
      });
      return best;
    }

    async _finish() {
      const type = this._computeType(this.state.answers);
      this.state.type = type;
      this._showResult(type);
      this._go('result');
      // Tag + (Flow/app) email. Fire-and-forget — the result is already on screen.
      this._post('result', { email: this.state.email, type });
    }

    _showResult(type) {
      this.querySelectorAll('.kz-result-pane').forEach((p) => {
        p.hidden = p.dataset.kvizResult !== type;
      });
      // fallback: if the matching metaobject is missing, show the first pane
      if (!this.querySelector(`.kz-result-pane[data-kviz-result="${type}"]`)) {
        const first = this.querySelector('.kz-result-pane');
        if (first) first.hidden = false;
      }
    }
  }

  customElements.define('kviz-wheel', KvizWheel);
}
